import React from 'react';
import { Box, Button, Text } from '@mantine/core';
import { motion, useReducedMotion } from 'framer-motion';
import FilterDates from './components/Filter.Dates';
import FilterRoot from './components/Filter.Root';
import FilterSection from './components/Filter.Section';
import FilterSort from './components/Filter.Sort';
import FilterViewHead from './components/Filter.View.Head';
import { dateSectionTitle } from './filter.date.label';
import type { TypeDateSection, TypeFilterSection, TypeListSort } from '@/types/list/types.list.filter';
import './filter.panel.css';

type Props = {
    sections: TypeFilterSection[];
    active: Record<string, string[]>;
    onToggle: (sectionId: string, value: string) => void;
    excluded?: Record<string, boolean>;
    onExclude?: (sectionId: string, value: boolean) => void;
    onClear: () => void;
    dates: TypeDateSection[];
    dateValues: Record<string, string>;
    onDateChange?: (sectionId: string, value: string) => void;
    sort?: TypeListSort;
    activeCount: number;
    onDone: () => void;
    initial?: string;
};

const NO_EXCLUDED: Record<string, boolean> = {};

const FilterPanel: React.FC<Props> = ({
    sections, active, onToggle, excluded = NO_EXCLUDED, onExclude, onClear, dates, dateValues, onDateChange, sort, activeCount, onDone, initial,
}) => {
    const reduced = useReducedMotion();
    const shown = sections.filter((section) => section.options.length > 0 || (active[section.id]?.length ?? 0) > 0);
    const hasSort = Boolean(sort && sort.options.length > 1);
    const destinations = [...shown.map((section) => `s:${section.id}`), ...(hasSort ? ['sort'] : []), ...dates.map((section) => `d:${section.id}`)];
    const only = destinations.length === 1 ? destinations[0] : null;
    const [view, setView] = React.useState<string>(initial ?? only ?? 'root');
    const back = only || initial ? undefined : () => setView('root');

    const section = view.startsWith('s:') ? shown.find((entry) => entry.id === view.slice(2)) ?? sections.find((entry) => entry.id === view.slice(2)) : undefined;
    const date = view.startsWith('d:') ? dates.find((entry) => entry.id === view.slice(2)) : undefined;

    const clearSection = (id: string) => {
        (active[id] ?? []).forEach((value) => onToggle(id, value));
        onExclude?.(id, false);
    };

    const body = section ? (
        <>
            <FilterViewHead
                title={section.label}
                onBack={back}
                onRemove={(active[section.id]?.length ?? 0) > 0 ? () => clearSection(section.id) : undefined}
            />
            <FilterSection
                section={section}
                selected={active[section.id] ?? []}
                excluded={Boolean(excluded[section.id])}
                onToggle={(value) => onToggle(section.id, value)}
                onExclude={onExclude ? (value) => onExclude(section.id, value) : undefined}
            />
        </>
    ) : view === 'sort' && sort ? (
        <>
            <FilterViewHead title="Ordina per" onBack={back} />
            <FilterSort sort={sort} />
        </>
    ) : date ? (
        <>
            <FilterViewHead
                title={dateSectionTitle(date.label)}
                onBack={back}
                onRemove={dateValues[date.id] ? () => onDateChange?.(date.id, '') : undefined}
            />
            <FilterDates section={date} value={dateValues[date.id] ?? ''} onChange={(value) => onDateChange?.(date.id, value)} />
        </>
    ) : destinations.length ? (
        <FilterRoot
            sections={shown}
            active={active}
            excluded={excluded}
            onToggle={onToggle}
            dates={dates}
            dateValues={dateValues}
            sort={hasSort ? sort : undefined}
            onOpen={setView}
        />
    ) : (
        <Text size="sm" c="dimmed" px="md" py="sm">Niente da filtrare in questa lista.</Text>
    );

    const forward = view !== 'root';

    return (
        <Box className="bd-fp">
            <Box className="bd-fp-head">
                <Text fw={700} size="sm" style={{ color: 'var(--bd-text-strong)' }}>Filtri</Text>
                <Text size="xs" c="dimmed">{activeCount ? `${activeCount} ${activeCount === 1 ? 'attivo' : 'attivi'}` : 'nessuno attivo'}</Text>
            </Box>

            <Box className="bd-fp-body">
                <motion.div
                    key={view}
                    initial={reduced ? false : { opacity: 0, x: forward ? 12 : -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                    {body}
                </motion.div>
            </Box>

            <Box className="bd-fp-foot">
                <Button variant="subtle" color="gray" size="sm" onClick={onClear} disabled={!activeCount}>Cancella filtri</Button>
                <Button size="sm" onClick={onDone}>Fatto</Button>
            </Box>
        </Box>
    );
};

export default FilterPanel;
